import React, { useState, useEffect, useMemo, type ChangeEvent } from 'react';
import { Modal, Button, Badge, Container, Row, Col, Form } from 'react-bootstrap';

// @ts-ignore - Fallback if @types/luxon is not installed in node_modules
import { DateTime } from 'luxon';

// --- Context & Hooks ---
import { useLanguage } from '../../../../API/Context/LanguageContext';
import { useUserAgenda } from '../../../../API/Hooks/useUserAgenda';
import { useActivePatient } from '../../../../API/Hooks/ActivePatientContext';

// --- Utils & Config ---
import { formatPatientShortName } from '../../../../Utils/patient';
import { getStatusDisplay } from '../../../../Utils/calendarUtils';
import { AgendaStatuses, type AgendaStatusType } from '../../../../ConfigFiles/agenda_status';
import { VocabProCalendarEventDetailModal as vocab } from '../../../../ConfigFiles/multilingual/VocabProCalendarEventDetailModal.js';
import './cssAnimations.css';

// --- Types ---
export interface Attendee {
  username?: string | null;
  name?: string;
  lastname_1?: string;
  lastname_2?: string;
  phone_number?: string | null;
  role?: string;
  status?: string;
  id_profile?: string | null;
}

export interface AgendaApiEvent {
  _id: string;
  uid?: string;
  start: string; // ISO 8601 UTC
  end: string;   // ISO 8601 UTC
  timezone?: string;
  time_zone?: string;
  location?: string;
  status?: AgendaStatusType;
  medic_username?: string;
  organizer_username?: string;
  title?: string;
  description?: string;
  attendees?: Attendee[];
}

export interface EventDataWrapper {
  apiEventData?: AgendaApiEvent;
  _id?: string;
  [key: string]: any;
}

interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventData: EventDataWrapper | AgendaApiEvent | null;
  onSuccess?: (updatedEvent?: any) => void;
}

interface FormDataState {
  start: string;
  end: string;
}

// Helper to safely extract translation string when lan is 0 (ES) or 1 (EN)
const getVocabText = (field: any, lan: number, fallback: string): string => {
  if (Array.isArray(field) && field[lan] !== undefined) {
    return field[lan];
  }
  if (field && typeof field === 'object' && field[lan] !== undefined) {
    return field[lan];
  }
  return fallback;
};

/**
 * Gets display formatted string for the appointment's target timezone (e.g., "CST -06:00")
 */
const getTimezoneDisplayName = (timezoneString?: string, isoDateString?: string): string => {
  if (!timezoneString) return 'Timezone N/A';

  const dt = isoDateString
    ? DateTime.fromISO(isoDateString).setZone(timezoneString)
    : DateTime.now().setZone(timezoneString);

  if (!dt?.isValid) return timezoneString;
  return dt.toFormat('z ZZZZ');
};

/**
 * Converts ISO string (UTC) to datetime-local input string format (YYYY-MM-DDThh:mm)
 */
const formatISOToInput = (isoString: string, targetZone: string): string => {
  if (!isoString) return '';
  const dt = DateTime.fromISO(isoString).setZone(targetZone);
  if (!dt?.isValid) return '';
  return dt.toFormat("yyyy-MM-dd'T'HH:mm");
};

export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  isOpen,
  onClose,
  eventData,
  onSuccess,
}) => {
  // --- Context Hooks ---
  // lan: 0 = Spanish, 1 = English
  const { lan = 0 } = (useLanguage() as { lan?: number }) || {};
  const { loadSelectedPatient } = useActivePatient();
  const {
    isLoading: isApiLoading,
    error: apiError,
    updateAgendaEntry,
  } = useUserAgenda();

  // Normalize API data structure
  const initialDetails: AgendaApiEvent = useMemo(() => {
    if (!eventData) return {} as AgendaApiEvent;
    if ('apiEventData' in eventData && eventData.apiEventData) {
      return eventData.apiEventData;
    }
    return eventData as AgendaApiEvent;
  }, [eventData]);

  const eventId = initialDetails._id;
  const primaryAttendee: Attendee = initialDetails.attendees?.[0] || {};
  
  // Pass object to formatPatientShortName to fix TS error 1
  const primaryAttendeeName = primaryAttendee.name || primaryAttendee.username
    ? formatPatientShortName({
        name: primaryAttendee.name || primaryAttendee.username || '',
        lastname_1: primaryAttendee.lastname_1 || '',
        lastname_2: primaryAttendee.lastname_2 || '',
      })
    : 'N/A';

  const primaryAttendeePhone = primaryAttendee.phone_number || 'N/A';
  const primaryAttendeeId = primaryAttendee.username || primaryAttendee.id_profile || null;

  const initialStart = initialDetails.start || '';
  const initialEnd = initialDetails.end || '';
  const targetTimezone = initialDetails.time_zone || initialDetails.timezone || 'America/Mexico_City';
  const isCancelled = initialDetails.status === AgendaStatuses.CANCELLED;

  // --- Local State ---
  const [formData, setFormData] = useState<FormDataState>({
    start: '',
    end: '',
  });
  const [isNavigatingPatient, setIsNavigatingPatient] = useState<boolean>(false);

  // Sync state on modal show or event load
  useEffect(() => {
    if (isOpen && eventId) {
      setFormData({
        start: initialStart,
        end: initialEnd,
      });
    }
  }, [isOpen, eventId, initialStart, initialEnd]);

  // Derived Timezone Label
  const tzDisplayName = useMemo(
    () => getTimezoneDisplayName(targetTimezone, formData.start || initialStart),
    [targetTimezone, formData.start, initialStart]
  );

  // Form modification check
  const hasDataChanged = useMemo(() => {
    return formData.start !== initialStart || formData.end !== initialEnd;
  }, [formData, initialStart, initialEnd]);

  if (!eventData || !isOpen) return null;

  // --- Change Handler ---
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (!value) {
      setFormData((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    // Convert input back to UTC ISO string using event's target timezone
    const localDt = DateTime.fromFormat(value, "yyyy-MM-dd'T'HH:mm", { zone: targetTimezone });

    if (localDt?.isValid) {
      const isoValue = localDt.toUTC().toISO() || '';
      setFormData((prev) => ({ ...prev, [name]: isoValue }));
    } else {
      console.warn(`Invalid date input for ${name}: ${value}`);
    }
  };

  // --- Patient Navigation Handler ---
  const handleAttendeeClick = async () => {
    if (!primaryAttendeeId || isNavigatingPatient) return;

    try {
      setIsNavigatingPatient(true);
      await loadSelectedPatient(primaryAttendeeId);
      onClose();
    } catch (err) {
      console.error('Failed to load patient profile:', err);
    } finally {
      setIsNavigatingPatient(false);
    }
  };

  // --- API Handlers ---
  const handleSave = async () => {
    if (!hasDataChanged || isCancelled || isApiLoading) return;

    // Fixed: 'status' uses AgendaStatuses.PENDING & 'time_zone' matching backend schema
    const updateData = {
      start: formData.start,
      end: formData.end,
      time_zone: targetTimezone,
      status: AgendaStatuses.PENDING,
    };

    try {
      const updatedEvent = await updateAgendaEntry(eventId, updateData);
      if (onSuccess) onSuccess(updatedEvent);
      onClose();
    } catch (error) {
      console.error('Update agenda entry failed:', error);
    }
  };

  const handleCancel = async () => {
    if (isCancelled || isApiLoading) return;

    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    // Fixed: 'status' uses typed AgendaStatuses.CANCELLED
    const cancelData = { status: AgendaStatuses.CANCELLED };

    try {
      const updatedEvent = await updateAgendaEntry(eventId, cancelData);
      if (onSuccess) onSuccess(updatedEvent);
      onClose();
    } catch (error) {
      console.error('Cancellation failed:', error);
    }
  };

  const isClickablePatient = Boolean(primaryAttendeeId);
  const statusInfo = getStatusDisplay(initialDetails.status || AgendaStatuses.NONE) || { variant: 'secondary' };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center flex-wrap gap-2 fs-5">
          <span>{initialDetails.title || 'Cita'}</span>
          <span className="text-muted">|</span>

          {/* Patient Name / Profile Link */}
          <span
            onClick={isClickablePatient ? handleAttendeeClick : undefined}
            style={{
              cursor: isClickablePatient ? 'pointer' : 'default',
              fontWeight: isClickablePatient ? 600 : 'normal',
              color: isClickablePatient ? 'var(--bs-primary)' : 'inherit',
            }}
            className={`d-inline-flex align-items-center ${
              isClickablePatient ? 'jiggle-highlight' : ''
            }`}
            title={isClickablePatient ? 'Click to view patient profile' : undefined}
          >
            {primaryAttendeeName}
            {isNavigatingPatient && (
              <span className="spinner-border spinner-border-sm ms-2" role="status" />
            )}
          </span>

          <span className="text-muted">|</span>

          <Badge bg={statusInfo.variant}>
            {initialDetails.status || 'UNKNOWN'}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {apiError && <div className="alert alert-danger py-2">{apiError}</div>}
        {isApiLoading && (
          <p className="text-info">
            {getVocabText(vocab.processing_request, lan, 'Processing...')}
          </p>
        )}

        <Container fluid className="px-0">
          <Row className="g-3">
            <Col md={4}>
              <Form.Group controlId="editStartTime">
                <Form.Label>
                  <strong>{getVocabText(vocab.start, lan, 'Start')}</strong>
                  <small className="text-muted ms-1">({tzDisplayName})</small>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="start"
                  value={formatISOToInput(formData.start, targetTimezone)}
                  onChange={handleChange}
                  disabled={isCancelled || isApiLoading}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="editEndTime">
                <Form.Label>
                  <strong>{getVocabText(vocab.end, lan, 'End')}</strong>
                  <small className="text-muted ms-1">({tzDisplayName})</small>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="end"
                  value={formatISOToInput(formData.end, targetTimezone)}
                  onChange={handleChange}
                  disabled={isCancelled || isApiLoading}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="patientPhone">
                <Form.Label>
                  <strong>{getVocabText(vocab.phone_number, lan, 'Phone Number')}</strong>
                </Form.Label>
                <div className="form-control-plaintext pt-1">
                  {primaryAttendeePhone}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Container>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button
          variant="danger"
          onClick={handleCancel}
          disabled={isCancelled || isApiLoading}
        >
          {isCancelled
            ? 'Already Cancelled'
            : isApiLoading
            ? 'Cancelling...'
            : 'Cancel Appointment'}
        </Button>

        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isApiLoading}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasDataChanged || isCancelled || isApiLoading}
          >
            {isApiLoading
              ? getVocabText(vocab.saving, lan, 'Saving...')
              : getVocabText(vocab.save_changes, lan, 'Save Changes')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EventDetailModal;